import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import {
  Building2,
  Sparkles,
  UserPlus,
  Plus,
  Trash2,
  Save,
  X,
  Edit3,
  Calendar,
  AlertTriangle,
  ChevronRight,
  Users,
  ArrowLeft,
  Search,
  FileSpreadsheet,
  KeyRound,
  Mail,
  MailX,
  ShieldAlert,
  Edit2,
  FileCheck2,
} from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";
import PasswordDisplayModal from "@/components/PasswordDisplayModal";
import LoginHistoryModal from "@/components/LoginHistoryModal";

// Formatta importi in €. Ritorna "—" se null/undefined/0 e includeZero=false.
const fmtEUR = (value, { includeZero = true } = {}) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (!includeZero && n === 0) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

const subscriptionState = (end) => {
  if (!end) return { label: "—", color: "text-slate-400" };
  const days = Math.ceil((new Date(end) - new Date()) / 86400000);
  if (days < 0) return { label: `Scaduto (${Math.abs(days)}gg fa)`, color: "text-red-600 font-semibold" };
  if (days < 30) return { label: `${days}gg alla scadenza`, color: "text-amber-600 font-semibold" };
  return { label: `Attivo (${days}gg)`, color: "text-emerald-600" };
};

// Restituisce classi TW + testo giorni per un badge demo colorato in base alla scadenza
const demoExpiryState = (demoExpiresAt) => {
  if (!demoExpiresAt) {
    return { classes: "text-slate-700 bg-slate-100", suffix: null, days: null };
  }
  const days = Math.ceil((new Date(demoExpiresAt) - new Date()) / 86400000);
  if (days < 0) return { classes: "text-red-700 bg-red-100", suffix: `scaduto da ${Math.abs(days)}gg`, days };
  if (days === 0) return { classes: "text-red-700 bg-red-100", suffix: "scade oggi", days };
  if (days <= 3) return { classes: "text-orange-700 bg-orange-100", suffix: `${days}gg`, days };
  if (days <= 7) return { classes: "text-amber-700 bg-amber-100", suffix: `${days}gg`, days };
  return { classes: "text-emerald-700 bg-emerald-100", suffix: `${days}gg`, days };
};

// Calcola stato sospensione lato frontend: manuale o auto per abbonamento scaduto.
const computeSuspension = (c) => {
  const manual = !!c?.is_suspended;
  let expired = false;
  if (c?.subscription_end) {
    const end = new Date(String(c.subscription_end).slice(0, 10));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expired = end < today;
  }
  return { suspended: manual || expired, reason: manual ? "manual" : (expired ? "expired" : null) };
};

// Calcola il "piano" a partire dai flag Plus/Premium.
// - Premium = tutti e 3 (PSV-TTF DA Mid + Alert Prezzi + Report Personalizzati)
// - Plus    = PSV-TTF DA Mid + Alert Prezzi (Report Personalizzati OFF)
// - Base    = nessuno dei 3 attivo
// - Personalizzato = qualunque altra combinazione
const computeTier = (c) => {
  const sg = !!c?.spot_gas_enabled;
  const pa = c?.price_alerts_enabled !== false;  // default ON
  const cr = !!c?.custom_reports_enabled;
  if (sg && pa && cr) {
    return { key: "premium", label: "PREMIUM", classes: "bg-purple-100 text-purple-800 border-purple-200", dot: "bg-purple-500" };
  }
  if (sg && pa && !cr) {
    return { key: "plus", label: "PLUS", classes: "bg-sky-100 text-sky-800 border-sky-200", dot: "bg-sky-500" };
  }
  if (!sg && !pa && !cr) {
    return { key: "base", label: "BASE", classes: "bg-slate-200 text-slate-700 border-slate-300", dot: "bg-slate-500" };
  }
  return { key: "custom", label: "PERSONALIZZATO", classes: "bg-yellow-100 text-yellow-800 border-yellow-300", dot: "bg-yellow-500" };
};

// Demo in cima → attivi → non attivi; dentro ogni gruppo per scadenza (senza data in fondo) o nome.
const compareCompanies = (sortBy) => (a, b) => {
  const demo = Number(!!b.is_demo_company) - Number(!!a.is_demo_company);
  if (demo) return demo;
  const act = Number(computeSuspension(a).suspended) - Number(computeSuspension(b).suspended);
  if (act) return act;
  if (sortBy === "expiry") {
    const ea = a.subscription_end ? String(a.subscription_end).slice(0, 10) : "9999-12-31";
    const eb = b.subscription_end ? String(b.subscription_end).slice(0, 10) : "9999-12-31";
    if (ea !== eb) return ea < eb ? -1 : 1;
  }
  return (a.name || "").localeCompare(b.name || "", "it", { sensitivity: "base" });
};

const STATUS_FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "active", label: "Attivi" },
  { key: "inactive", label: "Non attivi" },
];

const CompanyListControls = ({ statusFilter, setStatusFilter, sortBy, setSortBy, counts, crOnly, setCrOnly }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="companies-list-controls">
    <div className="flex items-center gap-2 flex-wrap">
    <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1" role="tablist">
      {STATUS_FILTERS.map((f) => (
        <button
          key={f.key}
          onClick={() => setStatusFilter(f.key)}
          className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${statusFilter === f.key ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid={`companies-filter-${f.key}`}
        >
          {f.label} <span className={`ml-1 text-xs ${statusFilter === f.key ? "text-blue-100" : "text-slate-400"}`}>{counts[f.key]}</span>
        </button>
      ))}
    </div>
    <button
      onClick={() => setCrOnly(!crOnly)}
      aria-pressed={crOnly}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-xl border font-medium transition-colors ${crOnly ? "bg-purple-600 border-purple-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-purple-400 hover:text-purple-700"}`}
      data-testid="companies-filter-custom-reports"
    >
      <FileCheck2 size={14} /> Report personalizzati
      <span className={`text-xs ${crOnly ? "text-purple-100" : "text-slate-400"}`}>{counts.cr}</span>
    </button>
    </div>
    <label className="flex items-center gap-2 text-sm text-slate-600">
      Ordina per
      <select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm outline-none focus:border-blue-500"
        data-testid="companies-sort"
      >
        <option value="expiry">Scadenza più vicina</option>
        <option value="name">Nome (A-Z)</option>
      </select>
    </label>
  </div>
);

export default function AdminUsers() {
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("expiry");
  const [crOnly, setCrOnly] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pdps, setPdps] = useState([]);  // Lista PdP (POD/PDR) dell'azienda selezionata
  const [newPdp, setNewPdp] = useState({ code: "", type: "power", label: "", address: "" });
  const [editingPdp, setEditingPdp] = useState(null);  // {id, code, type, label, address} in corso di edit
  const [confirmState, setConfirmState] = useState(null);
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: "", notes: "", subscription_start: "", subscription_end: "", custom_reports_enabled: false, spot_gas_enabled: false, price_alerts_enabled: true, market_reports_enabled: true, is_demo_company: false, subscription_fee: "", report_fee_per_pdp: "", pdp_count: "" });
  const [editingCompany, setEditingCompany] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", full_name: "", role: "cliente", is_demo: false, demo_expires_at: "" });
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === "admin";
  const [pwModal, setPwModal] = useState(null); // {user, password, mode}
  const [historyUser, setHistoryUser] = useState(null); // utente selezionato per storico login
  const [reactivateForm, setReactivateForm] = useState({ open: false, subscription_end: "" });

  const loadCompanies = async () => {
    const [{ data: cs }, { data: us }] = await Promise.all([
      api.get("/companies"),
      api.get("/users"),
    ]);
    setCompanies(cs);
    setAllUsers((us || []).filter((u) => u.role === "cliente"));
  };
  const loadDetail = async (cid) => {
    if (!cid) { setDetail(null); setPdps([]); return; }
    const { data } = await api.get(`/companies/${cid}`);
    setDetail(data);
    // Carica anche i PdP censiti
    try {
      const { data: list } = await api.get(`/companies/${cid}/pdps`);
      setPdps(Array.isArray(list) ? list : []);
    } catch (_e) { setPdps([]); }
    setEditForm({
      name: data.name || "",
      notes: data.notes || "",
      subscription_start: data.subscription_start || "",
      subscription_end: data.subscription_end || "",
      custom_reports_enabled: !!data.custom_reports_enabled,
      spot_gas_enabled: !!data.spot_gas_enabled,
      price_alerts_enabled: data.price_alerts_enabled !== false,  // default true
      market_reports_enabled: data.market_reports_enabled !== false,  // default true
      is_demo_company: !!data.is_demo_company,
      subscription_fee: data.subscription_fee ?? "",
      report_fee_per_pdp: data.report_fee_per_pdp ?? "",
      pdp_count: data.pdp_count ?? "",
    });
  };

  useEffect(() => { loadCompanies(); }, []);
  useEffect(() => { loadDetail(selectedId); setEditingCompany(false); setShowNewUser(false); }, [selectedId]);

  // Quando apro il form "Aggiungi utente" dentro un'azienda demo,
  // imposto is_demo=true di default con scadenza a 14 giorni (guardrail soft).
  useEffect(() => {
    if (showNewUser && detail?.is_demo_company && !newUser.is_demo) {
      setNewUser((prev) => ({
        ...prev,
        is_demo: true,
        demo_expires_at:
          prev.demo_expires_at ||
          new Date(Date.now() + 14 * 24 * 3600 * 1000).toISOString().slice(0, 10),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewUser, detail?.is_demo_company]);

  const createCompany = async () => {
    if (!newCompany.name.trim()) return toast.error("Nome obbligatorio");
    try {
      const { data } = await api.post("/companies", {
        name: newCompany.name.trim(),
        notes: newCompany.notes || null,
        subscription_start: newCompany.subscription_start || null,
        subscription_end: newCompany.subscription_end || null,
        custom_reports_enabled: newCompany.custom_reports_enabled,
        spot_gas_enabled: newCompany.spot_gas_enabled,
        price_alerts_enabled: newCompany.price_alerts_enabled,
        market_reports_enabled: newCompany.market_reports_enabled,
        is_demo_company: newCompany.is_demo_company,
        subscription_fee: newCompany.subscription_fee === "" ? 0 : parseFloat(newCompany.subscription_fee) || 0,
        report_fee_per_pdp: newCompany.report_fee_per_pdp === "" ? 0 : parseFloat(newCompany.report_fee_per_pdp) || 0,
        pdp_count: newCompany.pdp_count === "" ? 0 : parseInt(newCompany.pdp_count, 10) || 0,
      });
      toast.success("Azienda creata");
      setShowNewCompany(false);
      setNewCompany({ name: "", notes: "", subscription_start: "", subscription_end: "", custom_reports_enabled: false, spot_gas_enabled: false, price_alerts_enabled: true, market_reports_enabled: true, is_demo_company: false, subscription_fee: "", report_fee_per_pdp: "", pdp_count: "" });
      await loadCompanies();
      setSelectedId(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const saveCompany = async () => {
    try {
      await api.patch(`/companies/${selectedId}`, {
        name: editForm.name,
        notes: editForm.notes || null,
        subscription_start: editForm.subscription_start || null,
        subscription_end: editForm.subscription_end || null,
        custom_reports_enabled: editForm.custom_reports_enabled,
        spot_gas_enabled: editForm.spot_gas_enabled,
        price_alerts_enabled: editForm.price_alerts_enabled,
        market_reports_enabled: editForm.market_reports_enabled,
        is_demo_company: editForm.is_demo_company,
        subscription_fee: editForm.subscription_fee === "" ? 0 : parseFloat(editForm.subscription_fee) || 0,
        report_fee_per_pdp: editForm.report_fee_per_pdp === "" ? 0 : parseFloat(editForm.report_fee_per_pdp) || 0,
        pdp_count: editForm.pdp_count === "" ? 0 : parseInt(editForm.pdp_count, 10) || 0,
      });
      toast.success("Azienda aggiornata");
      setEditingCompany(false);
      await loadCompanies();
      await loadDetail(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const deleteCompany = () => {
    if (!detail) return;
    setConfirmState({
      title: "Eliminare l'azienda?",
      message: `"${detail.name}" verrà eliminata. Deve prima essere svuotata di utenti (attualmente ${detail.users?.length || 0}).`,
      confirmLabel: "Elimina", variant: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/companies/${selectedId}`);
          toast.success("Azienda eliminata");
          setSelectedId(null);
          await loadCompanies();
        } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
      },
    });
  };

  const addUser = async () => {
    if (!newUser.email.trim() || !newUser.password || !newUser.full_name.trim()) return toast.error("Compila tutti i campi");
    if (newUser.password.length < 6) return toast.error("La password deve avere almeno 6 caratteri");
    // Guardrail: se sto aggiungendo un utente NON demo in un'azienda demo → richiedo conferma esplicita
    if (detail?.is_demo_company && !newUser.is_demo) {
      setConfirmState({
        title: "Aggiungere un utente NON demo all'azienda demo?",
        message: `"${detail.name}" è un'azienda demo dedicata ai prospect. Un utente non-demo qui non avrà scadenza e potrà cambiare la password: potenzialmente resterà a vita nell'ambiente demo. Confermi?`,
        confirmLabel: "Sì, aggiungi come non-demo",
        variant: "danger",
        onConfirm: () => performAddUser(),
      });
      return;
    }
    performAddUser();
  };

  const performAddUser = async () => {
    try {
      const created = await api.post("/users", {
        email: newUser.email.trim(),
        password: newUser.password,
        full_name: newUser.full_name.trim(),
        role: "cliente",
        company_id: selectedId,
        is_demo: !!newUser.is_demo,
        demo_expires_at: newUser.is_demo ? (newUser.demo_expires_at || null) : null,
      });
      toast.success(newUser.is_demo ? "Utente demo aggiunto (scadenza 14gg se non specificata)" : "Utente aggiunto all'azienda");
      // Mostra password one-shot per fornirla al cliente
      setPwModal({
        user: { email: created.data?.email || newUser.email.trim(), full_name: created.data?.full_name || newUser.full_name.trim() },
        password: newUser.password,
        mode: "create",
      });
      setNewUser({ email: "", password: "", full_name: "", role: "cliente", is_demo: false, demo_expires_at: "" });
      setShowNewUser(false);
      await loadCompanies();
      await loadDetail(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const generatePassword = () => {
    // Genera una password sicura lato client per il campo "nuovo utente"
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const symbols = "!@#$%*?";
    let pw = "";
    for (let i = 0; i < 10; i++) pw += alphabet[Math.floor(Math.random() * alphabet.length)];
    pw += symbols[Math.floor(Math.random() * symbols.length)];
    pw += symbols[Math.floor(Math.random() * symbols.length)];
    return pw.split("").sort(() => Math.random() - 0.5).join("");
  };

  const resetUserPassword = (u) => {
    setConfirmState({
      title: "Reset password?",
      message: `Verrà generata una nuova password sicura per "${u.full_name || u.email}". La password originale non è recuperabile e questa nuova sarà mostrata una sola volta.`,
      confirmLabel: "Genera nuova password",
      variant: "primary",
      onConfirm: async () => {
        try {
          const { data } = await api.post(`/users/${u.id}/reset-password`);
          setPwModal({
            user: { email: data.email, full_name: data.full_name },
            password: data.password,
            mode: "reset-auto",
          });
          await loadDetail(selectedId);
        } catch (e) { toast.error(e.response?.data?.detail || "Errore reset password"); }
      },
    });
  };

  const removeUser = (u) => {
    setConfirmState({
      title: "Rimuovere l'utente?",
      message: `"${u.full_name || u.email}" verrà eliminato definitivamente.`,
      confirmLabel: "Elimina", variant: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/users/${u.id}`);
          toast.success("Utente eliminato");
          await loadCompanies();
          await loadDetail(selectedId);
        } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
      },
    });
  };

  const toggleEmailEnabled = async (u) => {
    // Admin can toggle a user's email opt-in directly without impersonating.
    // email_enabled defaults to True; false means the client has opted out.
    const current = u.email_enabled !== false; // treat missing/null as ON
    const next = !current;
    try {
      await api.patch(`/users/${u.id}`, { email_enabled: next });
      toast.success(next
        ? `Email abilitate per ${u.full_name || u.email}`
        : `Email disabilitate per ${u.full_name || u.email}`
      );
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const [editingUserId, setEditingUserId] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ full_name: "", email: "", password: "", is_demo: false, demo_expires_at: "" });
  const startEditUser = (u) => {
    setEditingUserId(u.id);
    setEditUserForm({
      full_name: u.full_name || "",
      email: u.email || "",
      password: "",
      is_demo: !!u.is_demo,
      demo_expires_at: (u.demo_expires_at || "").slice(0, 10),
    });
  };
  const saveEditUser = async () => {
    if (!editingUserId) return;
    const payload = {};
    if (editUserForm.full_name?.trim()) payload.full_name = editUserForm.full_name.trim();
    if (editUserForm.email?.trim()) payload.email = editUserForm.email.trim();
    if (editUserForm.password?.trim()) payload.password = editUserForm.password;
    payload.is_demo = !!editUserForm.is_demo;
    payload.demo_expires_at = editUserForm.is_demo ? (editUserForm.demo_expires_at || null) : null;
    if (Object.keys(payload).length === 0) {
      setEditingUserId(null);
      return;
    }
    // Guardrail: se sto salvando un utente NON demo dentro un'azienda demo → conferma esplicita
    if (detail?.is_demo_company && !payload.is_demo) {
      setConfirmState({
        title: "Salvare come utente NON demo in un'azienda demo?",
        message: `"${detail.name}" è un'azienda demo. Rimuovendo il flag demo, questo utente non scadrà mai e potrà cambiare password: resterà nell'ambiente vetrina per sempre. Confermi?`,
        confirmLabel: "Sì, salva come non-demo",
        variant: "danger",
        onConfirm: () => performSaveEditUser(payload),
      });
      return;
    }
    performSaveEditUser(payload);
  };

  const performSaveEditUser = async (payload) => {
    try {
      await api.patch(`/users/${editingUserId}`, payload);
      const user = detail?.users?.find((u) => u.id === editingUserId);
      if (payload.password) {
        setPwModal({
          user: { email: payload.email || user?.email, full_name: payload.full_name || user?.full_name },
          password: payload.password,
          mode: "reset",
        });
      } else {
        toast.success("Utente aggiornato");
      }
      setEditingUserId(null);
      setEditUserForm({ full_name: "", email: "", password: "", is_demo: false, demo_expires_at: "" });
      await loadCompanies();
      await loadDetail(selectedId);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // ---- Filtered list (search by company name OR user name/email) ----
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = companies.filter((c) => {
      if (crOnly && !c.custom_reports_enabled) return false;
      if (statusFilter === "all") return true;
      const inactive = computeSuspension(c).suspended;
      return statusFilter === "inactive" ? inactive : !inactive;
    });
    const sorted = [...base].sort(compareCompanies(sortBy));
    if (!term) return sorted.map((c) => ({ ...c, matchedUsers: [] }));
    return sorted
      .map((c) => {
        const nameMatch = (c.name || "").toLowerCase().includes(term);
        const usersOfCompany = allUsers.filter((u) => u.company_id === c.id);
        const matchedUsers = usersOfCompany.filter(
          (u) =>
            (u.full_name || "").toLowerCase().includes(term) ||
            (u.email || "").toLowerCase().includes(term),
        );
        return { ...c, matchedUsers, _matches: nameMatch || matchedUsers.length > 0 };
      })
      .filter((c) => c._matches);
  }, [q, companies, allUsers, statusFilter, sortBy, crOnly]);
  const counts = useMemo(() => {
    const inactive = companies.filter((c) => computeSuspension(c).suspended).length;
    const annual = (list) => list.reduce((t, c) => t + (Number(c.monthly_revenue) || 0) * 12, 0);
    return {
      all: companies.length, active: companies.length - inactive, inactive,
      cr: companies.filter((c) => c.custom_reports_enabled).length,
      revenueAll: annual(companies),
      revenueActive: annual(companies.filter((c) => !computeSuspension(c).suspended)),
    };
  }, [companies]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/companies/export.xlsx", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `adybo_clienti_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export Excel scaricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore export");
    } finally { setExporting(false); }
  };

  // ---- List view ----
  if (!selectedId) {
    return (
      <div className="space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Gestione Clienti</h1>
            <p className="text-sm text-slate-500 mt-1">Ogni cliente è un&apos;azienda con uno o più utenti che accedono all&apos;area riservata.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <div className="w-full sm:w-auto px-4 py-2 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 sm:text-right" data-testid="companies-total-revenue">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700/80">Totale abbonamenti annui</div>
              <div className="font-display text-xl font-bold text-emerald-700 leading-tight" data-testid="companies-total-revenue-value">{fmtEUR(counts.revenueAll)}</div>
              {counts.revenueActive !== counts.revenueAll && (
                <div className="text-[10px] text-slate-500" data-testid="companies-total-revenue-active">di cui attivi {fmtEUR(counts.revenueActive)}</div>
              )}
            </div>
            <button
              onClick={exportXlsx}
              disabled={exporting}
              className="text-sm px-3 py-2 rounded-lg border border-slate-200 hover:border-emerald-500 hover:text-emerald-600 flex items-center gap-2 disabled:opacity-50"
              data-testid="export-companies-xlsx"
            >
              <FileSpreadsheet size={16} /> {exporting ? "Esporto…" : "Esporta Excel"}
            </button>
            <button onClick={() => setShowNewCompany(true)} className="btn-brand flex items-center gap-2" data-testid="new-company-btn">
              <Plus size={16} /> Nuova azienda
            </button>
          </div>
        </div>

        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome azienda, nome utente o email…"
            className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-slate-200 text-sm bg-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            data-testid="companies-search"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded"
              aria-label="Reset"
              data-testid="companies-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <CompanyListControls statusFilter={statusFilter} setStatusFilter={setStatusFilter} sortBy={sortBy} setSortBy={setSortBy} counts={counts} crOnly={crOnly} setCrOnly={setCrOnly} />
        {showNewCompany && (
          <div className="card-elevated p-5 space-y-3" data-testid="new-company-form">
            <div className="flex items-center justify-between">
              <div className="font-display font-semibold">Nuova azienda</div>
              <button onClick={() => setShowNewCompany(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
            </div>
            <input value={newCompany.name} onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })} placeholder="Nome azienda" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="new-company-name" />
            <textarea value={newCompany.notes} onChange={(e) => setNewCompany({ ...newCompany, notes: e.target.value })} placeholder="Note (opzionale)" rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Inizio abbonamento</label>
                <input type="date" value={newCompany.subscription_start} onChange={(e) => setNewCompany({ ...newCompany, subscription_start: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Fine abbonamento</label>
                <input type="date" value={newCompany.subscription_end} onChange={(e) => setNewCompany({ ...newCompany, subscription_end: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" />
              </div>
            </div>
            <div className="pt-2 border-t border-slate-200/70">
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><Sparkles size={11} className="text-blue-500" /> Servizi extra</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="flex items-center gap-2 text-sm rounded-lg border border-slate-200 px-3 py-2">
                  <input type="checkbox" checked={newCompany.market_reports_enabled} onChange={(e) => setNewCompany({ ...newCompany, market_reports_enabled: e.target.checked })} data-testid="new-company-mr" />
                  <span className="text-slate-700"><span className="text-[9px] uppercase font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded mr-1">BASE</span>Report Mercati Energetici</span>
                </label>
                <label className="flex items-center gap-2 text-sm rounded-lg border border-slate-200 px-3 py-2">
                  <input type="checkbox" checked={newCompany.custom_reports_enabled} onChange={(e) => setNewCompany({ ...newCompany, custom_reports_enabled: e.target.checked })} data-testid="new-company-cr" />
                  <span className="text-slate-700"><span className="text-[9px] uppercase font-bold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded mr-1">PREMIUM</span>Report Personalizzati</span>
                </label>
                <label className="flex items-center gap-2 text-sm rounded-lg border border-slate-200 px-3 py-2" data-testid="new-company-spot-gas-label">
                  <input
                    type="checkbox"
                    checked={newCompany.spot_gas_enabled}
                    onChange={(e) => setNewCompany({ ...newCompany, spot_gas_enabled: e.target.checked })}
                    data-testid="new-company-spot-gas"
                  />
                  <span className="text-slate-700"><span className="text-[9px] uppercase font-bold bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded mr-1">PLUS</span>PSV - TTF DA Mid</span>
                </label>
                <label className="flex items-center gap-2 text-sm rounded-lg border border-slate-200 px-3 py-2" data-testid="new-company-price-alerts-label">
                  <input
                    type="checkbox"
                    checked={newCompany.price_alerts_enabled}
                    onChange={(e) => setNewCompany({ ...newCompany, price_alerts_enabled: e.target.checked })}
                    data-testid="new-company-price-alerts"
                  />
                  <span className="text-slate-700"><span className="text-[9px] uppercase font-bold bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded mr-1">PLUS</span>Alert Prezzi</span>
                </label>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50/60 border border-amber-200 rounded-lg px-3 py-2" data-testid="new-company-is-demo-label">
              <input
                type="checkbox"
                checked={newCompany.is_demo_company}
                onChange={(e) => setNewCompany({ ...newCompany, is_demo_company: e.target.checked })}
                data-testid="new-company-is-demo"
              />
              <span className="flex items-center gap-1.5"><Sparkles size={12} className="text-amber-600" /> Azienda dedicata agli account demo prospect <span className="text-[10px] text-amber-600 font-medium">(sarà evidenziata e sempre in cima alla lista)</span></span>
            </label>
            {/* Costi contrattuali (opzionali in fase di creazione) */}
            <div className="pt-2 border-t border-slate-200/70">
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Costi Contrattuali <span className="text-slate-400 normal-case text-[10px]">(opzionale · promemoria interno)</span></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                  <input type="number" min="0" step="0.01" value={newCompany.subscription_fee} onChange={(e) => setNewCompany({ ...newCompany, subscription_fee: e.target.value })} placeholder="Abbonamento /mese" className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="new-company-subscription-fee" />
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                  <input type="number" min="0" step="0.01" value={newCompany.report_fee_per_pdp} onChange={(e) => setNewCompany({ ...newCompany, report_fee_per_pdp: e.target.value })} placeholder="Report /mese/PdP" className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="new-company-report-fee-per-pdp" />
                </div>
                <input type="number" min="0" step="1" value={newCompany.pdp_count} onChange={(e) => setNewCompany({ ...newCompany, pdp_count: e.target.value })} placeholder="N° PdP" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="new-company-pdp-count" />
              </div>
            </div>
            <button onClick={createCompany} className="btn-brand flex items-center gap-2" data-testid="new-company-save">
              <Save size={14} /> Crea azienda
            </button>
          </div>
        )}
        <div className="card-elevated overflow-hidden" data-testid="companies-list">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              {q ? `Nessun risultato per "${q}"` : statusFilter !== "all" || crOnly ? "Nessun cliente corrisponde ai filtri." : "Nessuna azienda registrata."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((c) => {
                const sub = subscriptionState(c.subscription_end);
                const susp = computeSuspension(c);
                return (
                  <li key={c.id} data-testid={`company-row-${c.id}`} className={c.is_demo_company ? "bg-gradient-to-r from-amber-50/60 via-orange-50/40 to-transparent border-l-4 border-amber-400" : (susp.suspended ? "bg-gradient-to-r from-red-50/60 to-transparent border-l-4 border-red-400" : "")}>
                    <div
                      className={`px-5 py-3 flex items-center gap-3 cursor-pointer group ${
                        c.is_demo_company ? "hover:bg-amber-50/80" : "hover:bg-slate-50/60"
                      }`}
                      onClick={() => setSelectedId(c.id)}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md ${
                        c.is_demo_company
                          ? "bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-500/30"
                          : "bg-gradient-to-br from-blue-500 to-purple-600 shadow-blue-500/30"
                      }`}>
                        {c.is_demo_company
                          ? <Sparkles size={18} className="text-white" />
                          : <Building2 size={18} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                          <span className="truncate">{c.name}</span>
                          {susp.suspended && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full flex-shrink-0 bg-red-500 text-white shadow-sm"
                              title={susp.reason === "expired" ? "Abbonamento scaduto — accesso bloccato" : "Account sospeso manualmente"}
                              data-testid={`company-suspended-${c.id}`}
                            >
                              SOSPESO{susp.reason === "expired" ? " (SCADUTO)" : ""}
                            </span>
                          )}
                          {c.is_demo_company && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full flex-shrink-0 bg-amber-500 text-white shadow-sm"
                              title="Azienda dedicata agli account demo prospect"
                              data-testid={`company-is-demo-${c.id}`}
                            >
                              <Sparkles size={9} /> Azienda Demo
                            </span>
                          )}
                          {c.demo_users_count > 0 && !c.is_demo_company && (() => {
                            const s = demoExpiryState(c.demo_soonest_expiry);
                            return (
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full flex-shrink-0 ${s.classes}`}
                                title={c.demo_soonest_expiry
                                  ? `${c.demo_users_count} utente${c.demo_users_count === 1 ? "" : "i"} demo — prima scadenza: ${new Date(c.demo_soonest_expiry).toLocaleDateString("it-IT")}`
                                  : `${c.demo_users_count} utente${c.demo_users_count === 1 ? "" : "i"} demo`}
                                data-testid={`company-demo-badge-${c.id}`}
                              >
                                DEMO {c.demo_users_count > 1 && `×${c.demo_users_count}`}
                                {s.suffix && <span className="text-[9px] font-semibold opacity-90">· {s.suffix}</span>}
                              </span>
                            );
                          })()}
                          {c.is_demo_company && c.demo_users_count > 0 && (() => {
                            const s = demoExpiryState(c.demo_soonest_expiry);
                            return (
                              <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full flex-shrink-0 ${s.classes}`}>
                                {c.demo_users_count} utent{c.demo_users_count === 1 ? "e" : "i"}
                                {s.suffix && <span className="text-[9px] font-semibold opacity-90">· {s.suffix}</span>}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                          <span className="flex items-center gap-1"><Users size={11} /> {c.users_count} utent{c.users_count === 1 ? "e" : "i"}</span>
                          {(() => {
                            const tier = computeTier(c);
                            return (
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full border ${tier.classes}`}
                                title={`Piano ${tier.label}`}
                                data-testid={`company-tier-${c.id}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${tier.dot}`} />
                                {tier.label}
                              </span>
                            );
                          })()}
                          {c.custom_reports_enabled && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full bg-purple-600 text-white shadow-sm"
                              title="Report personalizzati / Controllo Fatture attivi"
                              data-testid={`company-custom-reports-${c.id}`}
                            >
                              <FileCheck2 size={10} /> Report personalizzati
                            </span>
                          )}
                          {c.market_reports_enabled === false && <span className="text-red-500 font-medium">· report mercati OFF</span>}
                          <span className={sub.color}><Calendar size={11} className="inline mr-1" />{sub.label}</span>
                        </div>
                      </div>
                      {Number(c.monthly_revenue) > 0 && (
                        <div
                          className="text-right flex-shrink-0"
                          title={`Ricavo annuo: (abbonamento ${fmtEUR(c.subscription_fee)}/mese + ${fmtEUR(c.report_fee_per_pdp)}/PdP × ${c.pdp_count || 0} PdP) × 12 = ${fmtEUR(Number(c.monthly_revenue) * 12)}/anno`}
                          data-testid={`company-annual-revenue-${c.id}`}
                        >
                          <div className={`font-display font-bold text-sm ${susp.suspended ? "text-slate-400 line-through" : "text-emerald-700"}`}>{fmtEUR(Number(c.monthly_revenue) * 12)}</div>
                          <div className="text-[10px] text-slate-400 uppercase tracking-wide">/anno</div>
                        </div>
                      )}
                      <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
                    </div>
                    {c.matchedUsers && c.matchedUsers.length > 0 && (
                      <div className="pl-16 pr-5 pb-3 -mt-1 flex flex-wrap gap-1.5" data-testid={`matched-users-${c.id}`}>
                        {c.matchedUsers.map((u) => (
                          <span
                            key={u.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] border border-blue-100"
                          >
                            <UserPlus size={10} /> {u.full_name || u.email}
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
        <PasswordDisplayModal
          open={!!pwModal}
          onClose={() => setPwModal(null)}
          user={pwModal?.user}
          password={pwModal?.password}
          mode={pwModal?.mode}
        />
      </div>
    );
  }

  // ---- Detail view ----
  if (!detail) return <div className="text-sm text-slate-500">Caricamento…</div>;
  const sub = subscriptionState(detail.subscription_end);
  return (
    <div className="space-y-5" data-testid="company-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => setSelectedId(null)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="back-to-companies"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Azienda</div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-3 flex-wrap">
            {detail.name}
            {(() => {
              const tier = computeTier(detail);
              return (
                <span
                  className={`inline-flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wide px-2.5 py-1 rounded-full border ${tier.classes}`}
                  title={`Piano attivo: ${tier.label}`}
                  data-testid="company-detail-tier"
                >
                  <span className={`w-2 h-2 rounded-full ${tier.dot}`} />
                  Piano {tier.label}
                </span>
              );
            })()}
            {(() => {
              const susp = computeSuspension(detail);
              if (!susp.suspended) return null;
              return (
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wide px-2.5 py-1 rounded-full border bg-red-100 text-red-800 border-red-300"
                  title={susp.reason === "expired" ? "Abbonamento scaduto — accesso cliente bloccato" : "Sospeso manualmente — accesso cliente bloccato"}
                  data-testid="company-detail-suspended"
                >
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  SOSPESO {susp.reason === "expired" ? "(SCADUTO)" : "(MANUALE)"}
                </span>
              );
            })()}
          </h1>
        </div>
        {!editingCompany && (() => {
          const susp = computeSuspension(detail);
          const manual = !!detail.is_suspended;
          // L'azienda è "in sospensione" se è stata sospesa manualmente OR per scadenza.
          // In entrambi i casi mostriamo "Riattiva accesso" (il modale si occupa
          // anche di rinnovare la scadenza se serve, caso expired).
          const needsReactivation = susp.suspended;
          const isExpired = susp.reason === "expired";
          const toggleSuspend = async () => {
            if (needsReactivation) {
              // Riattivazione → mini-form per impostare opzionalmente la nuova scadenza
              setReactivateForm({
                open: true,
                subscription_end: detail.subscription_end?.slice(0, 10) || "",
              });
              return;
            }
            try {
              await api.patch(`/companies/${selectedId}`, { is_suspended: true });
              toast.success("Azienda sospesa — gli utenti cliente non potranno più accedere");
              await loadDetail(selectedId);
              await loadCompanies();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Errore");
            }
          };
          const sendExpiredNow = async () => {
            if (!window.confirm(`Inviare ora l'email di "Abbonamento scaduto — accesso sospeso" a tutti gli utenti di ${detail.name}?`)) return;
            try {
              const { data } = await api.post(`/companies/${selectedId}/notify-subscription-expired`);
              toast.success(`Email inviata a ${data.recipients} utente/i`);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Errore invio email");
            }
          };
          return (
            <>
              <button
                onClick={toggleSuspend}
                className={`text-sm px-3 py-1.5 rounded-lg border flex items-center gap-1.5 ${
                  needsReactivation
                    ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    : "border-red-200 text-red-600 hover:bg-red-50"
                }`}
                data-testid="toggle-suspend-company"
                title={needsReactivation ? "Riattiva accesso dei clienti" : "Sospendi accesso dei clienti al portale"}
              >
                {needsReactivation ? "Riattiva accesso" : "Sospendi accesso"}
              </button>
              {isExpired && (
                <button
                  onClick={sendExpiredNow}
                  className="text-sm px-3 py-1.5 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 flex items-center gap-1.5"
                  data-testid="send-expired-email"
                  title="Invia subito l'email di avviso scadenza a tutti gli utenti dell'azienda"
                >
                  Invia email scadenza
                </button>
              )}
            </>
          );
        })()}
        {!editingCompany ? (
          <>
            <button onClick={() => setEditingCompany(true)} className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:border-blue-500 hover:text-blue-600 flex items-center gap-1.5" data-testid="edit-company"><Edit3 size={14} /> Modifica</button>
            {isAdmin && <button onClick={deleteCompany} className="text-sm px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 flex items-center gap-1.5" data-testid="delete-company"><Trash2 size={14} /> Elimina</button>}
          </>
        ) : (
          <>
            <button onClick={saveCompany} className="text-sm px-3 py-1.5 rounded-lg bg-slate-900 text-white flex items-center gap-1.5" data-testid="save-company"><Save size={14} /> Salva</button>
            <button onClick={() => setEditingCompany(false)} className="text-sm px-3 py-1.5 rounded-lg text-slate-500 hover:bg-slate-100">Annulla</button>
          </>
        )}
      </div>

      <div className="card-elevated p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Nome azienda</label>
            {editingCompany ? (
              <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="edit-name" />
            ) : (
              <div className="mt-1 text-sm font-semibold text-slate-900">{detail.name}</div>
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Abbonamento</label>
            {editingCompany ? (
              <div className="grid grid-cols-2 gap-1 mt-1">
                <input type="date" value={editForm.subscription_start} onChange={(e) => setEditForm({ ...editForm, subscription_start: e.target.value })} className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-blue-500" data-testid="edit-sub-start" />
                <input type="date" value={editForm.subscription_end} onChange={(e) => setEditForm({ ...editForm, subscription_end: e.target.value })} className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-blue-500" data-testid="edit-sub-end" />
              </div>
            ) : (
              <div className={`mt-1 text-sm ${sub.color}`}>{sub.label}</div>
            )}
          </div>
        </div>

        {/* --- Servizi extra: raggruppati per piano (Base / Plus / Premium) --- */}
        <div className="mt-5 pt-4 border-t border-slate-200/70" data-testid="servizi-extra-section">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3 flex items-center gap-2">
            <Sparkles size={12} className="text-blue-500" /> Servizi extra
          </div>
          {(() => {
            // Definizione servizi extra raggruppati per piano
            const groups = [
              {
                title: "Servizio BASE",
                accent: "border-slate-300 bg-slate-50/60",
                pillClass: "bg-slate-200 text-slate-700",
                services: [
                  { key: "market_reports_enabled", label: "Report Mercati Energetici", testid: "mr", activeText: "text-slate-700", defaultOn: true },
                ],
              },
              {
                title: "Servizio PLUS",
                accent: "border-sky-200 bg-sky-50/40",
                pillClass: "bg-sky-100 text-sky-800",
                services: [
                  { key: "spot_gas_enabled", label: "PSV - TTF DA Mid", testid: "sg", activeText: "text-sky-700" },
                  { key: "price_alerts_enabled", label: "Alert Prezzi", testid: "pa", activeText: "text-sky-700", defaultOn: true },
                ],
              },
              {
                title: "Servizio PREMIUM",
                accent: "border-purple-200 bg-purple-50/40",
                pillClass: "bg-purple-100 text-purple-800",
                services: [
                  { key: "custom_reports_enabled", label: "Report Personalizzati", testid: "cr", activeText: "text-purple-700" },
                  { key: "energy_analytics_enabled", label: "Energy Analytics", testid: "ea", activeText: "text-purple-700" },
                ],
              },
            ];
            const detailValue = (s) => s.defaultOn ? detail[s.key] !== false : !!detail[s.key];
            return (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {groups.map((g) => (
                  <div key={g.title} className={`rounded-xl border p-3 ${g.accent}`} data-testid={`group-${g.title.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`inline-block text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${g.pillClass}`}>
                        {g.title}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {g.services.map((s) => {
                        const enabled = editingCompany ? !!editForm[s.key] : detailValue(s);
                        return (
                          <div key={s.key} className="flex items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 px-3 py-2" data-testid={`service-${s.testid}`}>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-800 truncate">{s.label}</div>
                              <div className={`text-[11px] font-medium ${enabled ? s.activeText : "text-slate-400"}`}>
                                {enabled ? "Abilitato" : "Disabilitato"}
                              </div>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={enabled}
                              onClick={async () => {
                                if (editingCompany) {
                                  setEditForm({ ...editForm, [s.key]: !enabled });
                                } else {
                                  try {
                                    await api.patch(`/companies/${selectedId}`, { [s.key]: !enabled });
                                    toast.success(!enabled ? `${s.label} abilitato` : `${s.label} disabilitato`);
                                    await loadDetail(selectedId);
                                    await loadCompanies();
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || "Errore");
                                  }
                                }
                              }}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${enabled ? "bg-emerald-500" : "bg-slate-300"}`}
                              data-testid={`toggle-${s.testid}`}
                              title={enabled ? "Disabilita" : "Abilita"}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
        {editingCompany && (
          <div className="mt-3 space-y-3">
            <label className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50/60 border border-amber-200 rounded-lg px-3 py-2">
              <input
                type="checkbox"
                checked={!!editForm.is_demo_company}
                onChange={(e) => setEditForm({ ...editForm, is_demo_company: e.target.checked })}
                data-testid="edit-is-demo-company"
              />
              <span className="flex items-center gap-1.5"><Sparkles size={12} className="text-amber-600" /> Azienda dedicata agli account demo prospect</span>
            </label>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Note (opzionale)</label>
              <textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" />
            </div>
          </div>
        )}
        {!editingCompany && detail.notes && (
          <div className="mt-3 text-sm text-slate-600 border-l-2 border-slate-200 pl-3">{detail.notes}</div>
        )}
      </div>

      {/* --- Costi Contrattuali (promemoria interno admin) --- */}
      <div className="card-elevated p-5" data-testid="contract-costs-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
              <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-sm shadow-emerald-500/30 text-xs font-bold">€</span>
              Costi Contrattuali
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">Promemoria interno · visibile solo agli amministratori</div>
          </div>
          {!editingCompany && (() => {
            const subFee = Number(detail.subscription_fee) || 0;
            const repFee = Number(detail.report_fee_per_pdp) || 0;
            const pdp = Number(detail.pdp_count) || 0;
            const total = subFee + repFee * pdp;
            if (total <= 0) return null;
            return (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ricavo annuo</div>
                <div className="text-2xl font-bold text-emerald-700 font-display" data-testid="yearly-revenue-total">
                  {fmtEUR(total * 12)}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mt-1">Ricavo mensile</div>
                <div className="text-sm font-semibold text-emerald-600" data-testid="monthly-revenue-total">
                  {fmtEUR(total)}
                </div>
              </div>
            );
          })()}
        </div>
        {editingCompany ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Abbonamento (€/mese)</label>
                <div className="mt-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={editForm.subscription_fee}
                    onChange={(e) => setEditForm({ ...editForm, subscription_fee: e.target.value })}
                    placeholder="0,00"
                    className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                    data-testid="edit-subscription-fee"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Report Personalizzati (€/mese/PdP)</label>
                <div className="mt-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={editForm.report_fee_per_pdp}
                    onChange={(e) => setEditForm({ ...editForm, report_fee_per_pdp: e.target.value })}
                    placeholder="0,00"
                    className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                    data-testid="edit-report-fee-per-pdp"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Numero PdP</label>
                <input
                  type="number" min="0" step="1"
                  value={editForm.pdp_count}
                  onChange={(e) => setEditForm({ ...editForm, pdp_count: e.target.value })}
                  placeholder="0"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                  data-testid="edit-pdp-count"
                />
                <div className="text-[10px] text-slate-400 mt-1">Punti di Prelievo</div>
              </div>
            </div>
            {(() => {
              const subFee = parseFloat(editForm.subscription_fee) || 0;
              const repFee = parseFloat(editForm.report_fee_per_pdp) || 0;
              const pdp = parseInt(editForm.pdp_count, 10) || 0;
              const reportTotal = repFee * pdp;
              const total = subFee + reportTotal;
              if (total <= 0) return null;
              return (
                <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-emerald-50 via-teal-50 to-transparent border border-emerald-200/60" data-testid="annual-revenue-preview">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs text-slate-600">
                      <span className="font-medium">Ricavo annuo stimato:</span>{" "}
                      <span className="text-slate-500">({fmtEUR(subFee)} abbonamento{pdp > 0 ? ` + ${fmtEUR(repFee)} × ${pdp} PdP (${fmtEUR(reportTotal)})` : ""}) × 12</span>
                    </div>
                    <div className="text-lg font-bold text-emerald-700 font-display">= {fmtEUR(total * 12)}<span className="text-xs font-normal opacity-70">/anno · {fmtEUR(total)}/mese</span></div>
                  </div>
                </div>
              );
            })()}
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Abbonamento</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {fmtEUR(detail.subscription_fee, { includeZero: false })}
                {Number(detail.subscription_fee) > 0 && <span className="text-xs font-normal text-slate-500">/mese</span>}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Report Personalizzati</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {fmtEUR(detail.report_fee_per_pdp, { includeZero: false })}
                {Number(detail.report_fee_per_pdp) > 0 && <span className="text-xs font-normal text-slate-500">/mese/PdP</span>}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Numero PdP</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {detail.pdp_count > 0 ? (
                  <>{detail.pdp_count} <span className="text-xs font-normal text-slate-500">punt{detail.pdp_count === 1 ? "o" : "i"} di prelievo</span></>
                ) : "—"}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="card-elevated overflow-hidden" data-testid="pdps-card">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center text-[10px] font-bold">PdP</span>
            Punti di Prelievo (POD / PDR) <span className="text-xs font-normal text-slate-400">({pdps.length})</span>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-[11px] text-slate-500">
            Censisci qui i POD (energia elettrica · <span className="font-mono">IT001E...</span>) e PDR (gas · 14 cifre) dell'azienda.
            Verranno proposti nel menù a tendina durante l'upload del Controllo Fatture.
          </p>
          {/* Form inline nuovo PDP */}
          <div className="grid grid-cols-1 sm:grid-cols-[90px,1fr,1fr,1.5fr,auto] gap-2 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Tipo</label>
              <select
                value={newPdp.type}
                onChange={(e) => setNewPdp({ ...newPdp, type: e.target.value })}
                className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm"
                data-testid="new-pdp-type"
              >
                <option value="power">Power</option>
                <option value="gas">Gas</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Codice {newPdp.type === "gas" ? "PDR" : "POD"}</label>
              <input
                type="text"
                value={newPdp.code}
                onChange={(e) => setNewPdp({ ...newPdp, code: e.target.value })}
                placeholder={newPdp.type === "gas" ? "14 cifre" : "IT001E..."}
                className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm font-mono"
                data-testid="new-pdp-code"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Etichetta (opz.)</label>
              <input
                type="text"
                value={newPdp.label}
                onChange={(e) => setNewPdp({ ...newPdp, label: e.target.value })}
                placeholder="es. Sede Milano"
                className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm"
                data-testid="new-pdp-label"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Indirizzo fornitura (opz.)</label>
              <input
                type="text"
                value={newPdp.address}
                onChange={(e) => setNewPdp({ ...newPdp, address: e.target.value })}
                placeholder="es. Via Roma 10, 20100 Milano"
                className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm"
                data-testid="new-pdp-address"
              />
            </div>
            <button
              onClick={async () => {
                if (!newPdp.code.trim()) return toast.error("Inserisci il codice");
                try {
                  await api.post(`/companies/${selectedId}/pdps`, newPdp);
                  toast.success("PdP aggiunto");
                  setNewPdp({ code: "", type: newPdp.type, label: "", address: "" });
                  await loadDetail(selectedId);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Errore");
                }
              }}
              className="text-sm px-3 py-1.5 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-700 whitespace-nowrap"
              data-testid="add-pdp-btn"
            >
              + Aggiungi
            </button>
          </div>
          {pdps.length > 0 && (
            <div className="border border-slate-100 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold w-16">Tipo</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Codice</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Etichetta</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Indirizzo fornitura</th>
                    <th className="text-right px-3 py-1.5 font-semibold w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {pdps.map((p) => {
                    const editing = editingPdp?.id === p.id;
                    return (
                      <tr key={p.id} className="border-t border-slate-100">
                        <td className="px-3 py-1.5">
                          {editing ? (
                            <select
                              value={editingPdp.type}
                              onChange={(e) => setEditingPdp({ ...editingPdp, type: e.target.value })}
                              className="px-1.5 py-0.5 rounded border border-slate-200 text-xs"
                              data-testid={`edit-pdp-type-${p.id}`}
                            >
                              <option value="power">power</option>
                              <option value="gas">gas</option>
                            </select>
                          ) : (
                            <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded-full ${p.type === "gas" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                              {p.type}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs text-slate-700">
                          {editing ? (
                            <input
                              type="text"
                              value={editingPdp.code}
                              onChange={(e) => setEditingPdp({ ...editingPdp, code: e.target.value })}
                              className="w-full px-1.5 py-0.5 rounded border border-slate-200 text-xs font-mono"
                              data-testid={`edit-pdp-code-${p.id}`}
                            />
                          ) : (
                            <div>
                              <div>{p.code}</div>
                              {p.type === "gas" && p.remi_code && (
                                <div className="text-[10px] text-slate-400" data-testid={`pdp-remi-${p.id}`}>
                                  {p.remi_code === p.code ? "REMI · diretta rete Snam" : `REMI ${p.remi_code}`}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600">
                          {editing ? (
                            <input
                              type="text"
                              value={editingPdp.label}
                              onChange={(e) => setEditingPdp({ ...editingPdp, label: e.target.value })}
                              placeholder="es. Sede Milano"
                              className="w-full px-1.5 py-0.5 rounded border border-slate-200 text-xs"
                              data-testid={`edit-pdp-label-${p.id}`}
                            />
                          ) : (p.label || "—")}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600 text-xs">
                          {editing ? (
                            <input
                              type="text"
                              value={editingPdp.address}
                              onChange={(e) => setEditingPdp({ ...editingPdp, address: e.target.value })}
                              placeholder="es. Via Roma 10, 20100 Milano"
                              className="w-full px-1.5 py-0.5 rounded border border-slate-200 text-xs"
                              data-testid={`edit-pdp-address-${p.id}`}
                            />
                          ) : (p.address || "—")}
                        </td>
                        <td className="px-3 py-1.5 text-right whitespace-nowrap">
                          {editing ? (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={async () => {
                                  try {
                                    await api.patch(`/companies/${selectedId}/pdps/${p.id}`, {
                                      code: editingPdp.code,
                                      type: editingPdp.type,
                                      label: editingPdp.label,
                                      address: editingPdp.address,
                                    });
                                    toast.success("PdP aggiornato");
                                    setEditingPdp(null);
                                    await loadDetail(selectedId);
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || "Errore");
                                  }
                                }}
                                className="text-xs px-2 py-0.5 rounded bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                                data-testid={`save-pdp-${p.id}`}
                              >Salva</button>
                              <button
                                onClick={() => setEditingPdp(null)}
                                className="text-xs px-2 py-0.5 rounded text-slate-500 hover:bg-slate-100"
                              >Annulla</button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-2 text-slate-400">
                              <button
                                onClick={() => setEditingPdp({ id: p.id, code: p.code, type: p.type, label: p.label || "", address: p.address || "" })}
                                className="hover:text-blue-600"
                                title="Modifica"
                                data-testid={`edit-pdp-${p.id}`}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Rimuovere il PdP ${p.code}?`)) return;
                                  try {
                                    await api.delete(`/companies/${selectedId}/pdps/${p.id}`);
                                    toast.success("PdP rimosso");
                                    await loadDetail(selectedId);
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || "Errore");
                                  }
                                }}
                                className="hover:text-red-600"
                                title="Rimuovi"
                                data-testid={`del-pdp-${p.id}`}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display font-semibold text-slate-900 flex items-center gap-2"><Users size={16} className="text-blue-600" /> Utenti ({detail.users?.length || 0})</div>
          <button onClick={() => setShowNewUser(true)} className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:border-blue-500 hover:text-blue-600 flex items-center gap-1.5" data-testid="add-user-btn"><UserPlus size={14} /> Aggiungi utente</button>
        </div>
        {showNewUser && (
          <div className="p-5 border-b border-slate-100 space-y-2 bg-blue-50/30">
            {detail?.is_demo_company && (
              <div
                className={`rounded-lg p-3 border text-xs flex items-start gap-2 ${
                  newUser.is_demo
                    ? "bg-amber-50 border-amber-200 text-amber-800"
                    : "bg-red-50 border-red-300 text-red-800"
                }`}
                data-testid="new-user-demo-company-warning"
              >
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">
                    Sei nell&apos;azienda demo {`"${detail.name}"`}.
                  </div>
                  {newUser.is_demo ? (
                    <div className="mt-0.5">
                      L&apos;utente sarà demo con scadenza automatica (default 14gg). Perfetto per un prospect.
                    </div>
                  ) : (
                    <div className="mt-0.5">
                      Attenzione: hai disattivato <b>Account demo</b>. Un utente non-demo qui non scadrà mai
                      e potrà cambiare password: <b>resterà per sempre nell&apos;ambiente vetrina</b>.
                      Ti verrà chiesta una conferma esplicita al salvataggio.
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} placeholder="Nome e cognome" className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="new-user-name" />
              <input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="Email" className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="new-user-email" />
              <div className="flex gap-1">
                <input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Password (min 6)" className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500 font-mono" data-testid="new-user-password" />
                <button
                  type="button"
                  onClick={() => setNewUser({ ...newUser, password: generatePassword() })}
                  className="px-2.5 py-2 rounded-lg border border-slate-200 text-xs text-slate-600 hover:border-blue-500 hover:text-blue-600 whitespace-nowrap"
                  title="Genera password sicura"
                  data-testid="new-user-generate-pw"
                >
                  Genera
                </button>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 flex items-start gap-1.5">
              <AlertTriangle size={11} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <span>La password sarà mostrata una sola volta al termine della creazione. Copiala e condividila con l&apos;utente tramite un canale sicuro.</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-slate-200/70 mt-1">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="new-user-is-demo-label">
                <input
                  type="checkbox"
                  checked={newUser.is_demo}
                  onChange={(e) => setNewUser({ ...newUser, is_demo: e.target.checked, demo_expires_at: e.target.checked && !newUser.demo_expires_at
                    ? new Date(Date.now() + 14*24*3600*1000).toISOString().slice(0,10) : newUser.demo_expires_at })}
                  data-testid="new-user-is-demo"
                />
                <span>Account demo <span className="text-[10px] text-slate-400">(non può cambiare password)</span></span>
              </label>
              {newUser.is_demo && (
                <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <span className="text-xs text-slate-500">Scadenza:</span>
                  <input
                    type="date"
                    value={newUser.demo_expires_at}
                    onChange={(e) => setNewUser({ ...newUser, demo_expires_at: e.target.value })}
                    className="px-2 py-1 rounded-lg border border-slate-200 text-xs outline-none focus:border-blue-500"
                    data-testid="new-user-demo-expires"
                  />
                  <span className="text-[10px] text-slate-400">default 14 giorni</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={addUser} className="btn-brand text-sm !py-1.5 !px-3 flex items-center gap-1.5" data-testid="new-user-save"><Save size={14} /> Aggiungi</button>
              <button onClick={() => setShowNewUser(false)} className="text-sm px-3 py-1.5 text-slate-500 hover:text-slate-700">Annulla</button>
            </div>
          </div>
        )}
        {(!detail.users || detail.users.length === 0) ? (
          <div className="p-8 text-center text-sm text-slate-500">Nessun utente in questa azienda. Aggiungine uno per abilitare l&apos;accesso.</div>
        ) : (
          <table className="data-table w-full">
            <thead>
              <tr>
                <th>Nome</th><th>Email</th><th>Creato</th><th className="text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {detail.users.map((u) => {
                const isEditing = editingUserId === u.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    {isEditing ? (
                      <>
                        <td colSpan={4} className="!p-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <input
                              value={editUserForm.full_name}
                              onChange={(e) => setEditUserForm({ ...editUserForm, full_name: e.target.value })}
                              placeholder="Nome e cognome"
                              className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                              data-testid={`edit-user-name-${u.id}`}
                            />
                            <input
                              value={editUserForm.email}
                              onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                              placeholder="Email"
                              className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                              data-testid={`edit-user-email-${u.id}`}
                            />
                            <input
                              type="password"
                              value={editUserForm.password}
                              onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })}
                              placeholder="Nuova password (lascia vuoto per non modificare)"
                              className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                              data-testid={`edit-user-password-${u.id}`}
                            />
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                            <AlertTriangle size={12} className="text-amber-500" />
                            <span>Per motivi di sicurezza la password originale non è visibile. Imposta una nuova password se necessario.</span>
                          </div>
                          <div className="mt-3 pt-3 border-t border-slate-200/70 flex flex-wrap items-center gap-3">
                            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editUserForm.is_demo}
                                onChange={(e) => setEditUserForm({
                                  ...editUserForm,
                                  is_demo: e.target.checked,
                                  demo_expires_at: e.target.checked && !editUserForm.demo_expires_at
                                    ? new Date(Date.now() + 14*24*3600*1000).toISOString().slice(0,10)
                                    : editUserForm.demo_expires_at,
                                })}
                                data-testid={`edit-user-is-demo-${u.id}`}
                              />
                              <span>Account demo <span className="text-[10px] text-slate-400">(non può cambiare password)</span></span>
                            </label>
                            {editUserForm.is_demo && (
                              <div className="inline-flex items-center gap-2 text-sm text-slate-700">
                                <span className="text-xs text-slate-500">Scadenza:</span>
                                <input
                                  type="date"
                                  value={editUserForm.demo_expires_at || ""}
                                  onChange={(e) => setEditUserForm({ ...editUserForm, demo_expires_at: e.target.value })}
                                  className="px-2 py-1 rounded-lg border border-slate-200 text-xs outline-none focus:border-blue-500"
                                  data-testid={`edit-user-demo-expires-${u.id}`}
                                />
                              </div>
                            )}
                          </div>
                          <div className="mt-3 flex items-center gap-2">
                            <button onClick={saveEditUser} className="btn-brand text-sm !py-1.5 !px-3 flex items-center gap-1.5" data-testid={`save-user-${u.id}`}>
                              <Save size={14} /> Salva
                            </button>
                            <button
                              onClick={() => { setEditingUserId(null); setEditUserForm({ full_name: "", email: "", password: "" }); }}
                              className="text-sm px-3 py-1.5 text-slate-500 hover:text-slate-700"
                            >
                              Annulla
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="!font-sans font-semibold">
                          {u.full_name}
                          {u.is_demo && (() => {
                            const s = demoExpiryState(u.demo_expires_at);
                            return (
                              <span
                                className={`ml-2 inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full align-middle ${s.classes}`}
                                title={u.demo_expires_at ? `Scade il ${new Date(u.demo_expires_at).toLocaleDateString("it-IT")}` : "Account demo"}
                                data-testid={`user-demo-badge-${u.id}`}
                              >
                                DEMO
                                {s.suffix && <span className="text-[9px] font-semibold opacity-90">· {s.suffix}</span>}
                              </span>
                            );
                          })()}
                          {u.email_enabled === false && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full align-middle bg-red-50 text-red-700 border border-red-200"
                              title="Il cliente ha disattivato la ricezione delle email dal proprio Profilo. Non riceverà notifiche via email."
                              data-testid={`user-email-off-badge-${u.id}`}
                            >
                              📭 EMAIL OFF
                            </span>
                          )}
                        </td>
                        <td className="!font-sans text-slate-600">{u.email}</td>
                        <td className="!font-sans text-xs">{fmtDateTime(u.created_at)}</td>
                        <td className="text-right whitespace-nowrap">
                          <button
                            onClick={() => toggleEmailEnabled(u)}
                            className={`p-1.5 mr-1 ${u.email_enabled === false ? "text-red-500 hover:text-red-700" : "text-slate-500 hover:text-emerald-600"}`}
                            title={u.email_enabled === false ? "Email disattivate — clicca per RIABILITARE la ricezione" : "Email abilitate — clicca per DISATTIVARE la ricezione"}
                            data-testid={`toggle-email-user-${u.id}`}
                          >
                            {u.email_enabled === false ? <MailX size={15} /> : <Mail size={15} />}
                          </button>
                          <button
                            onClick={() => resetUserPassword(u)}
                            className="p-1.5 text-slate-500 hover:text-purple-600 mr-1"
                            title="Genera nuova password"
                            data-testid={`reset-pw-user-${u.id}`}
                          >
                            <KeyRound size={15} />
                          </button>
                          {isAdmin && (
                          <button
                            onClick={() => setHistoryUser(u)}
                            className="p-1.5 text-slate-500 hover:text-amber-600 mr-1"
                            title="Sessioni & IP"
                            data-testid={`login-history-user-${u.id}`}
                          >
                            <ShieldAlert size={15} />
                          </button>
                          )}
                          <button
                            onClick={() => startEditUser(u)}
                            className="p-1.5 text-slate-500 hover:text-blue-600 mr-1"
                            title="Modifica utente"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Edit3 size={15} />
                          </button>
                          {isAdmin && (
                          <button onClick={() => removeUser(u)} className="p-1.5 text-red-500 hover:text-red-700" title="Rimuovi utente" data-testid={`remove-user-${u.id}`}>
                            <Trash2 size={15} />
                          </button>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {detail.custom_reports_count > 0 && (
        <div className="text-xs text-slate-500 flex items-center gap-1 px-1">
          <AlertTriangle size={11} className="text-amber-500" />
          {detail.custom_reports_count} report personalizzat{detail.custom_reports_count !== 1 ? "i" : "o"} associat{detail.custom_reports_count !== 1 ? "i" : "o"} — visibil{detail.custom_reports_count !== 1 ? "i" : "e"} a tutti gli utenti dell&apos;azienda.
        </div>
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
      <PasswordDisplayModal
        open={!!pwModal}
        onClose={() => setPwModal(null)}
        user={pwModal?.user}
        password={pwModal?.password}
        mode={pwModal?.mode}
      />
      {historyUser && (
        <LoginHistoryModal user={historyUser} onClose={() => setHistoryUser(null)} />
      )}

      {/* --- Mini-modale Riattivazione con rinnovo scadenza --- */}
      {reactivateForm.open && (() => {
        // Rileva se la scadenza attuale è nel passato: in quel caso il campo
        // "nuova scadenza" è obbligatorio altrimenti il cliente resta bloccato
        // dalla auto-sospensione anche dopo il reset di `is_suspended`.
        const currentEnd = detail?.subscription_end?.slice(0, 10) || "";
        const isCurrentlyExpired = (() => {
          if (!currentEnd) return false;
          const end = new Date(currentEnd);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return end < today;
        })();
        const typedEnd = reactivateForm.subscription_end;
        const typedEndIsFuture = (() => {
          if (!typedEnd) return false;
          const end = new Date(typedEnd);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return end >= today;
        })();
        // La nuova scadenza è REQUIRED quando l'abbonamento attuale è scaduto.
        const requiresNewEnd = isCurrentlyExpired;
        const canConfirm = !requiresNewEnd || typedEndIsFuture;
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="reactivate-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">✓</span>
                Riattiva accesso
              </h3>
              <button onClick={() => setReactivateForm({ open: false, subscription_end: "" })} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-600">
                Stai per riattivare l&apos;accesso di <b>{detail?.name}</b> al portale.
                {requiresNewEnd ? (
                  <> L&apos;abbonamento è <b>scaduto</b>, quindi devi obbligatoriamente indicare una <b>nuova data di fine</b> altrimenti il cliente resterà bloccato.</>
                ) : (
                  <> Puoi contestualmente aggiornare la data di fine abbonamento, oppure lasciarla invariata.</>
                )}
              </p>
              <div>
                <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                  {requiresNewEnd ? "Nuova fine abbonamento *" : "Nuova fine abbonamento"}
                  {!requiresNewEnd && <span className="text-slate-400 normal-case text-[10px] ml-1">(opzionale)</span>}
                </label>
                <input
                  type="date"
                  value={reactivateForm.subscription_end}
                  onChange={(e) => setReactivateForm({ ...reactivateForm, subscription_end: e.target.value })}
                  className={`mt-1 w-full px-3 py-2 rounded-lg border text-sm outline-none focus:border-blue-500 ${
                    requiresNewEnd && !typedEndIsFuture ? "border-red-300 bg-red-50/40" : "border-slate-200"
                  }`}
                  data-testid="reactivate-sub-end"
                />
                <div className="text-[11px] text-slate-400 mt-1">
                  {requiresNewEnd ? (
                    <span className="text-red-600 font-medium">La data deve essere oggi o nel futuro.</span>
                  ) : (
                    <>Lascia vuoto per mantenere l&apos;attuale valore ({currentEnd || "nessuna scadenza"}).</>
                  )}
                </div>
              </div>
              <div className="rounded-lg bg-blue-50/60 border border-blue-200 px-3 py-2 text-xs text-blue-800 flex items-start gap-2">
                <Mail size={14} className="flex-shrink-0 mt-0.5" />
                <span>Agli utenti dell&apos;azienda verrà inviata un&apos;email di ripristino con la nuova scadenza.</span>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/60">
              <button
                onClick={() => setReactivateForm({ open: false, subscription_end: "" })}
                className="text-sm px-3 py-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
                data-testid="reactivate-cancel"
              >
                Annulla
              </button>
              <button
                disabled={!canConfirm}
                onClick={async () => {
                  if (!canConfirm) return;
                  try {
                    const payload = { is_suspended: false };
                    if (reactivateForm.subscription_end) {
                      payload.subscription_end = reactivateForm.subscription_end;
                    }
                    await api.patch(`/companies/${selectedId}`, payload);
                    toast.success("Accesso riattivato — email di notifica inviata ai clienti");
                    setReactivateForm({ open: false, subscription_end: "" });
                    await loadDetail(selectedId);
                    await loadCompanies();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Errore");
                  }
                }}
                className={`text-sm px-4 py-1.5 rounded-lg text-white font-semibold flex items-center gap-1.5 ${
                  canConfirm ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-300 cursor-not-allowed"
                }`}
                data-testid="reactivate-confirm"
              >
                <Save size={14} /> Conferma riattivazione
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
